/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-unknown-property */
import Head from 'next/head';
import { getCurrentHub } from '@sentry/nextjs';
import { ComponentProps } from '../ContentComposer';
import HeaderImageComponent from './Image';

export type HeaderImageProps = {
  url: string;
  alt?: string;
  caption: string | string[] | ComponentProps[];
  srcset?: { [key: number]: string };
  // Achata o canto superior da capa quando há tarja de editoria colada acima.
  hasTarja?: boolean;
  isHeader?: 'internal' | 'full';
};

// Catálogo de larguras aceitas para gerar descritores `w` no `srcset`
// compatíveis com as derivações de imagem entregues pela API. Cada chave
// é o próprio `w` declarado — alinhado com o `IMAGE_SRCSET` do servidor.
export const srcSetDictionary = {
  320: 320,
  640: 640,
  768: 768,
  1024: 1024,
  1200: 1200,
};

export default function HeaderImage({ url, alt, caption, srcset, hasTarja = undefined, isHeader = undefined }: HeaderImageProps) {
  const currentSpan = getCurrentHub().getScope()?.getSpan();
  const headerImageSpan = currentSpan && currentSpan.startChild({ op: 'headerImage' });
  const imageProps = {};

  if (srcset) {
    Object.assign(imageProps, {
      imagesizes: '(max-width: 992px) 100vw, 60vw',
      imagesrcset: Object.keys(srcset).map(e => ` ${srcset[e]} ${srcSetDictionary[e]}w`),
    });
  }

  // Fallback degradante para o `alt` do leitor de tela:
  //   1. `alt` explícito quando truthy após `trim` (matéria do Directus com
  //      texto curado/auto-importado, ou string informativa vinda do `NewsItem`);
  //   2. `caption` quando for string simples (compat com callers institucionais
  //      que passam apenas `caption`);
  //   3. `''` quando `caption` é array de `ComponentProps` (caption rica não
  //      serializa de forma útil como `alt` — `.toString()` produziria
  //      `'[object Object]'`).
  // `trim()` evita que valores só com whitespace (CMS frequentemente emite
  // strings tipo `'   '`) sejam truthy e bloqueiem o fallback semântico.
  const trimmedAlt = typeof alt === 'string' ? alt.trim() : '';
  const trimmedCaption = typeof caption === 'string' ? caption.trim() : '';
  const accessibleAlt = trimmedAlt || trimmedCaption;

  return (
    <div className="mx-0 mb-6 mt-0 w-full">
      <Head>
        <link rel="preload" as="image" href={url} {...imageProps} {...({ fetchpriority: 'high' } as any)} />
      </Head>
      <HeaderImageComponent src={url} alt={accessibleAlt} figcaption={caption} srcset={srcset} hasTarja={hasTarja} isHeader={isHeader} />
      {headerImageSpan && headerImageSpan.finish()}
    </div>
  );
}
